/**
 * BrainDiagnostic — super-admin retrieval debugger.
 *
 * WHY: Callers report the concierge saying "no information" about a document
 * that IS uploaded. We can't guess our way through six retrieval layers. This
 * panel calls GET /api/admin/diagnostics/brain-snapshot and renders the
 * result as a decision tree — where in the pipeline did the doc get dropped?
 *
 * Field-driven: built after the "hardship-leasing" incident (2026-02) where
 * we spent multiple attempts guessing at the failure. This is the one screen
 * that answers definitively.
 *
 * Access: super_admin / platform_admin only (enforced server-side).
 */
import React, { useEffect, useState } from "react";
import { PageTitle } from "@/components/PageHeader";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { BrainCircuit, Search, AlertTriangle, CheckCircle2, XCircle, Info, Loader2 } from "lucide-react";

const Chip = ({ ok, text }) => (
  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold ${
    ok ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
       : "bg-rose-50 text-rose-700 border border-rose-200"}`}>
    {ok ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />} {text}
  </span>
);

const Section = ({ title, children, testid }) => (
  <div className="bg-white rounded-2xl border border-sand-200 p-5" data-testid={testid}>
    <div className="text-[11px] font-bold uppercase tracking-wider text-ink/60 mb-3">{title}</div>
    {children}
  </div>
);

const KV = ({ k, v }) => (
  <div className="flex items-baseline justify-between gap-4 py-1.5 border-b border-sand-100 last:border-0">
    <span className="text-xs text-ink/60">{k}</span>
    <span className="text-sm text-ink font-medium text-right break-all">{String(v ?? "—")}</span>
  </div>
);

/**
 * iter206 — Expanded trace detail. Renders the `extra` payload for a trace
 * row in a way that's actually READABLE:
 *   • evidence_preview → table of top-5 chunks the LLM got (title/section/cosine/text)
 *   • brain_preview / user_message_preview → the exact text handed to GPT
 *   • partial_stats → sub-step timings + last_phase (for brain_timeout rows)
 *   • everything else → pretty-printed key/value list
 * The point: answer "what is the LLM actually seeing?" from THIS panel, not
 * by digging in Mongo or grep'ing production logs.
 */
const TraceDetail = ({ extra, kind }) => {
  const ev = extra?.evidence_preview;
  const brain = extra?.brain_preview;
  const userMsg = extra?.user_message_preview;
  const fullAns = extra?.full_answer;
  const partial = extra?.partial_stats;
  // Other extras — everything not already surfaced in a dedicated block.
  const otherEntries = Object.entries(extra || {}).filter(
    ([k]) => !["evidence_preview", "brain_preview", "user_message_preview",
                "full_answer", "partial_stats", "args"].includes(k)
  );
  return (
    <div className="space-y-3 text-sm" data-testid="trace-detail">
      {kind === "VT_CALL" && extra?.args && (
        <div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-ink/60 mb-1">Tool arguments</div>
          <pre className="text-[11px] font-mono bg-white border border-sand-200 rounded p-2 whitespace-pre-wrap break-all">{JSON.stringify(extra.args, null, 2)}</pre>
        </div>
      )}
      {partial && (
        <div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-rose-700 mb-1">Partial stats (brain build was cancelled)</div>
          <div className="bg-white border border-rose-200 rounded p-2 text-xs">
            <div><b>last_phase:</b> <span className="font-mono text-rose-700">{partial.last_phase || "—"}</span> <span className="text-ink/50">(the sub-step running when the cap fired)</span></div>
            {partial.identity_ms != null && <div><b>identity_ms:</b> {partial.identity_ms} ms</div>}
            {partial.parallel_load_ms != null && <div><b>parallel_load_ms:</b> {partial.parallel_load_ms} ms</div>}
            {partial.evidence_ms != null && <div><b>evidence_ms:</b> {partial.evidence_ms} ms</div>}
            {partial.evidence_sub_phase && (
              <div className="mt-1 pt-1 border-t border-rose-100">
                <b>evidence_sub_phase:</b> <span className="font-mono text-rose-700">{partial.evidence_sub_phase}</span> <span className="text-ink/50">(the piece of retrieval that was running)</span>
              </div>
            )}
            {partial.evidence_embed_ms != null && <div>  ↳ <b>evidence_embed_ms:</b> {partial.evidence_embed_ms} ms <span className="text-ink/50">(OpenAI Embeddings API)</span></div>}
            {partial.evidence_fetch_ms != null && <div>  ↳ <b>evidence_fetch_ms:</b> {partial.evidence_fetch_ms} ms <span className="text-ink/50">(Mongo chunk fetch)</span></div>}
            {partial.evidence_cosine_ms != null && <div>  ↳ <b>evidence_cosine_ms:</b> {partial.evidence_cosine_ms} ms <span className="text-ink/50">(in-memory scoring loop)</span></div>}
            {partial.evidence_pool_size != null && <div>  ↳ <b>evidence_pool_size:</b> {partial.evidence_pool_size} chunks scored</div>}
          </div>
        </div>
      )}
      {ev && ev.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-ink/60 mb-1">Top evidence the LLM received ({ev.length})</div>
          <div className="bg-white border border-sand-200 rounded overflow-hidden">
            <table className="w-full text-[11px]">
              <thead className="bg-sand-50">
                <tr className="text-ink/50 uppercase text-[9px]">
                  <th className="text-left py-1 px-2">Cosine</th>
                  <th className="text-left py-1 px-2">Document</th>
                  <th className="text-left py-1 px-2">Section</th>
                  <th className="text-left py-1 px-2">Text snippet</th>
                </tr>
              </thead>
              <tbody>
                {ev.map((c, i) => (
                  <tr key={i} className="border-t border-sand-100 align-top">
                    <td className="py-1 px-2 font-mono">{c.score?.toFixed?.(3) ?? c.score}</td>
                    <td className="py-1 px-2">{c.title || "—"}</td>
                    <td className="py-1 px-2 text-ink/60">{c.section || "—"}</td>
                    <td className="py-1 px-2 text-ink/70">{c.text_preview}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {userMsg && (
        <div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-ink/60 mb-1">Exact user message sent to GPT (first 600 chars)</div>
          <pre className="text-[11px] font-mono bg-white border border-sand-200 rounded p-2 whitespace-pre-wrap break-all max-h-48 overflow-auto">{userMsg}</pre>
        </div>
      )}
      {brain && (
        <div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-ink/60 mb-1">Brain preview (first 1200 chars of the assembled community brief)</div>
          <pre className="text-[11px] font-mono bg-white border border-sand-200 rounded p-2 whitespace-pre-wrap break-all max-h-64 overflow-auto">{brain}</pre>
        </div>
      )}
      {fullAns && (
        <div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-emerald-700 mb-1">GPT&apos;s full answer (what synth_answer contained)</div>
          <pre className="text-[11px] font-mono bg-white border border-emerald-200 rounded p-2 whitespace-pre-wrap break-all max-h-64 overflow-auto">{fullAns}</pre>
        </div>
      )}
      {otherEntries.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-ink/60 mb-1">Metadata</div>
          <div className="bg-white border border-sand-200 rounded p-2 text-[11px] font-mono">
            {otherEntries.map(([k, v]) => (
              <div key={k}><span className="text-ink/50">{k}=</span><span className="text-ink break-all">{typeof v === "object" ? JSON.stringify(v) : String(v)}</span></div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default function BrainDiagnostic() {
  // Redline-admins only. The API already 403s everyone else, but managers/board could
  // still land here via an old deep-link — bounce them out so it's truly super-admin-only.
  const { user } = useAuth();
  const navigate = useNavigate();
  const allowed = ["super_admin", "platform_admin"].includes(user?.role);
  useEffect(() => {
    if (user && !allowed) navigate("/dashboard", { replace: true });
  }, [user, allowed, navigate]);
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState("");
  const [query, setQuery] = useState("");
  const [docHint, setDocHint] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  // Voice trace state — populated by the /voice-traces endpoint. This is what
  // makes the "did the model even call search_documents?" question a
  // point-and-click answer (iter204b).
  const [traces, setTraces] = useState([]);
  const [tracesLoading, setTracesLoading] = useState(false);
  // iter206 — click-to-expand map so each trace row can reveal its full
  // extra JSON (evidence_preview, brain_preview, user_message_preview,
  // partial_stats etc). Truncated one-liner extras were hiding EXACTLY the
  // data a super-admin needs to see "what the LLM actually got handed."
  const [expandedRows, setExpandedRows] = useState({});

  useEffect(() => {
    api.get("/tenants").then((r) => setTenants(r.data || [])).catch(() => setTenants([]));
  }, []);

  const run = async () => {
    if (!tenantId) { toast.error("Pick a community"); return; }
    if (!query.trim()) { toast.error("Enter the caller's question"); return; }
    setLoading(true);
    setResult(null);
    try {
      const r = await api.get("/admin/diagnostics/brain-snapshot", {
        params: { tenant_id: tenantId, query: query.trim(), doc_hint: docHint.trim(), phone: phone.trim() },
      });
      setResult(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Diagnostic failed");
    } finally {
      setLoading(false);
    }
  };

  const loadTraces = async () => {
    setTracesLoading(true);
    try {
      const r = await api.get("/admin/diagnostics/voice-traces", {
        params: { tenant_id: tenantId, limit: 100 },
      });
      setTraces(r.data?.events || []);
      if ((r.data?.events || []).length === 0) {
        toast.info(tenantId
          ? "No voice traces yet — place a voice call on this community, then reload."
          : "No voice traces yet across all communities.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load voice traces");
    } finally {
      setTracesLoading(false);
    }
  };

  if (user && !allowed) return null;

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="brain-diagnostic-page">
      <div>
        <PageTitle title={<><BrainCircuit className="h-7 w-7 text-redline-500" strokeWidth={1.5} /> Brain Diagnostic</>} about={<>Reproduce a caller&apos;s exact retrieval path — see which documents the brain considered,
          which chunks made the pool, their cosine scores against the query, and where (if
          anywhere) a fully-indexed doc got dropped. No LLM call, no writes.</>} />
      </div>

      <div className="bg-white rounded-2xl border border-sand-200 p-6 space-y-4" data-testid="brain-diagnostic-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-ink/60 mb-1.5 block">Community *</label>
            <select data-testid="diag-tenant" value={tenantId} onChange={(e) => setTenantId(e.target.value)}
              className="w-full rounded-xl border border-sand-200 px-4 py-2.5 outline-none focus:ring-2 focus:ring-redline-500/30 bg-white">
              <option value="">— pick a community —</option>
              {tenants.map((t) => (
                <option key={t.id} value={t.id}>{t.name} ({t.id})</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-ink/60 mb-1.5 block">Caller phone (optional)</label>
            <input data-testid="diag-phone" value={phone} onChange={(e) => setPhone(e.target.value)}
              placeholder="+15125550012 — blank = anonymous stranger"
              className="w-full rounded-xl border border-sand-200 px-4 py-2.5 outline-none focus:ring-2 focus:ring-redline-500/30" />
          </div>
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-ink/60 mb-1.5 block">Caller&apos;s question *</label>
          <input data-testid="diag-query" value={query} onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && run()}
            placeholder="what are the hardship leasing requirements?"
            className="w-full rounded-xl border border-sand-200 px-4 py-2.5 outline-none focus:ring-2 focus:ring-redline-500/30" />
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-ink/60 mb-1.5 block">Document hint (optional)</label>
          <input data-testid="diag-dochint" value={docHint} onChange={(e) => setDocHint(e.target.value)}
            placeholder="hardship — matches doc title / filename / summary / tags"
            className="w-full rounded-xl border border-sand-200 px-4 py-2.5 outline-none focus:ring-2 focus:ring-redline-500/30" />
        </div>
        <div className="flex justify-end">
          <button data-testid="diag-run" disabled={loading} onClick={run}
            className="inline-flex items-center gap-2 rounded-full bg-redline-500 text-white font-semibold px-6 py-2.5 hover:bg-redline-600 transition-colors disabled:opacity-50">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            {loading ? "Running…" : "Run diagnostic"}
          </button>
        </div>
      </div>

      {/* iter204b — Voice Traces panel. Place ONE voice call, then click "Load recent voice traces"
          to see EXACTLY which tools the Realtime model invoked and what the AI said each turn.
          The 3 unambiguous patterns:
            • No VT_CALL row for search_documents after a policy question → model bypassed the tool
            • VT_CALL then VSD_FALLBACK reason=synth_timeout → synth cap too tight on prod latency
            • VSD_SUCCESS but AI_SAID doesn't match the synth_answer → model ignored the tool result */}
      <div className="bg-white rounded-2xl border border-sand-200 p-6 space-y-3" data-testid="voice-trace-panel">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="font-heading font-extrabold text-ink text-lg">Voice call trace</div>
            <p className="text-sm text-muted-foreground mt-0.5">
              After you place a real voice call, click below to see which tools the concierge invoked
              and what it said out loud. 24h retention.
            </p>
          </div>
          <button data-testid="voice-trace-load" onClick={loadTraces} disabled={tracesLoading}
            className="inline-flex items-center gap-2 rounded-full border border-sand-200 hover:border-redline-500 hover:text-redline-500 bg-white font-semibold px-4 py-2 transition-colors disabled:opacity-50">
            {tracesLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            {tracesLoading ? "Loading…" : (traces.length === 0 ? "Load recent voice traces" : `Reload (${traces.length})`)}
          </button>
        </div>
        {traces.length > 0 && (
          <div className="overflow-x-auto -mx-2 px-2" data-testid="voice-trace-list">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-ink/50 uppercase text-[10px] tracking-wider">
                  <th className="text-left py-1.5 pr-3 w-6"></th>
                  <th className="text-left py-1.5 pr-3">When</th>
                  <th className="text-left py-1.5 pr-3">Kind</th>
                  <th className="text-left py-1.5 pr-3">Phone</th>
                  <th className="text-left py-1.5 pr-3">Detail</th>
                  <th className="text-left py-1.5">Extra</th>
                </tr>
              </thead>
              <tbody>
                {traces.map((e, i) => {
                  const kindColor = e.kind === "VSD_SUCCESS" ? "text-emerald-700 bg-emerald-50"
                    : e.kind === "VSD_FALLBACK" ? "text-rose-700 bg-rose-50"
                    : e.kind === "VSD_BRAIN" ? "text-indigo-700 bg-indigo-50"
                    : e.kind === "VT_CALL" ? "text-blue-700 bg-blue-50"
                    : e.kind === "VT_RESULT" ? "text-slate-700 bg-slate-100"
                    : e.kind === "AI_SAID" ? "text-purple-700 bg-purple-50"
                    : e.kind === "VSD_REJECT" ? "text-amber-800 bg-amber-50"
                    : "text-slate-600 bg-slate-50";
                  const when = e.created_at ? new Date(e.created_at).toLocaleTimeString(undefined, { hour12: false }) : "—";
                  const extraStr = e.extra ? Object.entries(e.extra).filter(([, v]) => v !== null && v !== undefined && v !== "").map(([k, v]) => `${k}=${typeof v === "object" ? JSON.stringify(v) : v}`).join("  ") : "";
                  const isExpanded = expandedRows[i];
                  const hasExtra = e.extra && Object.keys(e.extra).length > 0;
                  return (
                    <React.Fragment key={i}>
                      <tr className="border-t border-sand-100 align-top hover:bg-sand-50 cursor-pointer"
                        onClick={() => hasExtra && setExpandedRows(r => ({...r, [i]: !r[i]}))}
                        data-testid={`voice-trace-row-${i}`}>
                        <td className="py-2 pr-1 font-mono text-[10px] text-ink/40 select-none">{hasExtra ? (isExpanded ? "▼" : "▶") : " "}</td>
                        <td className="py-2 pr-3 font-mono text-[11px] text-ink/50 whitespace-nowrap">{when}</td>
                        <td className="py-2 pr-3"><span className={`inline-block rounded px-1.5 py-0.5 font-mono text-[10px] font-bold ${kindColor}`}>{e.kind}</span></td>
                        <td className="py-2 pr-3 font-mono text-[11px] text-ink/60">…{e.phone_tail}</td>
                        <td className="py-2 pr-3 text-ink/80 max-w-md break-words">{e.message}</td>
                        <td className="py-2 text-ink/60 font-mono text-[10px] max-w-xs truncate">{extraStr}</td>
                      </tr>
                      {isExpanded && hasExtra && (
                        <tr className="border-t border-sand-100 bg-sand-50/50">
                          <td colSpan={6} className="p-4">
                            <TraceDetail extra={e.extra} kind={e.kind} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {traces.length === 0 && !tracesLoading && (
          <div className="text-sm text-muted-foreground italic bg-sand-50 rounded-lg p-4">
            No voice traces loaded yet. Place a voice call, then click <b>Load recent voice traces</b>.
            You&apos;ll see each tool the concierge invoked + what it said out loud, tagged by phone last-4.
          </div>
        )}
      </div>

      {result && (
        <div className="space-y-4" data-testid="diag-result">
          <Section title="Diagnosis" testid="diag-diagnosis">
            {(result.diagnosis || []).length === 0 ? (
              <div className="text-sm text-ink/50">No diagnosis lines returned.</div>
            ) : (
              <ul className="space-y-2">
                {result.diagnosis.map((line, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" strokeWidth={1.75} />
                    <span className="text-ink">{line}</span>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Section title="Identity gates" testid="diag-gates">
              <KV k="Viewer tier" v={result.identity_gates?.viewer} />
              <KV k="Person matched" v={result.identity_gates?.person_matched ? `Yes — ${result.identity_gates?.person_name || "(no name)"}` : "No"} />
              <KV k="Board privileges" v={result.identity_gates?.include_board ? "Yes" : "No"} />
              <KV k="Roster-verified" v={result.identity_gates?.verified_roster_gate ? "Yes" : "No"} />
              <KV k="Item-verified (voice code)" v={result.identity_gates?.item_verified_code_gate ? "Yes" : "No"} />
            </Section>
            <Section title="Chunk pool stats" testid="diag-pool">
              <KV k="Library size (this viewer)" v={result.chunk_pool_stats?.library_total_chunks_for_this_viewer} />
              <KV k="Pool size (fetched)" v={result.chunk_pool_stats?.pool_size} />
              <KV k="Chunks with embedding" v={result.chunk_pool_stats?.pool_chunks_with_embedding} />
              <KV k="Unique docs in pool" v={result.chunk_pool_stats?.unique_documents_in_pool} />
              <KV k="Staged retrieval used" v={result.chunk_pool_stats?.staged_retrieval_used ? "Yes" : "No"} />
              <KV k="Evidence chunk cap" v={result.chunk_pool_stats?.evidence_chunk_cap} />
              {result.chunk_pool_stats?.embed_error && <KV k="Embed error" v={result.chunk_pool_stats.embed_error} />}
            </Section>
          </div>

          {/* iter206 — Live timing breakdown. Answers the question the voice
              `brain_timeout` trace couldn't: WHICH sub-step blew the budget?
              If brain_build_total_ms is near the voice cap (4s), and one of
              parallel_load_ms / evidence_ms dominates, THAT is the root
              cause — not the timeout wrapper. */}
          {(result.brain_build_stats || result.timing_breakdown) && (
            <Section title="Live latency breakdown (this exact query, no timeout cap)" testid="diag-timings">
              {result.brain_build_stats?.error ? (
                <div className="text-sm text-rose-600 font-mono">{result.brain_build_stats.error}</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-ink/50 font-semibold mb-2">Brain build (production path)</div>
                    <KV k="Total brain build" v={result.brain_build_stats?.brain_build_total_ms != null ? `${result.brain_build_stats.brain_build_total_ms} ms` : "—"} />
                    <KV k="  ↳ Identity resolve" v={result.brain_build_stats?.identity_ms != null ? `${result.brain_build_stats.identity_ms} ms` : "—"} />
                    <KV k="  ↳ Parallel-load (facts/rules/docs)" v={result.brain_build_stats?.parallel_load_ms != null ? `${result.brain_build_stats.parallel_load_ms} ms` : "—"} />
                    <KV k="  ↳ Evidence retrieval (total)" v={result.brain_build_stats?.evidence_ms != null ? `${result.brain_build_stats.evidence_ms} ms` : "—"} />
                    <KV k="      ↳ embed_query (OpenAI)" v={result.brain_build_stats?.evidence_embed_ms != null ? `${result.brain_build_stats.evidence_embed_ms} ms` : "—"} />
                    <KV k="      ↳ chunk fetch (Mongo)" v={result.brain_build_stats?.evidence_fetch_ms != null ? `${result.brain_build_stats.evidence_fetch_ms} ms` : "—"} />
                    <KV k="      ↳ cosine loop (in-mem)" v={result.brain_build_stats?.evidence_cosine_ms != null ? `${result.brain_build_stats.evidence_cosine_ms} ms` : "—"} />
                    <KV k="      pool size (chunks scored)" v={result.brain_build_stats?.evidence_pool_size ?? "—"} />
                    <KV k="Voice brain cap" v={result.brain_build_stats?.voice_brain_timeout_s != null ? `${result.brain_build_stats.voice_brain_timeout_s} s` : "—"} />
                    <KV k="Would time out on voice?" v={
                      result.brain_build_stats?.would_time_out_on_voice
                        ? <span className="text-rose-600 font-bold">YES — this exact query would fail live</span>
                        : <span className="text-emerald-700 font-bold">No — fits under voice cap</span>
                    } />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-ink/50 font-semibold mb-2">Diagnostic sub-steps (for context)</div>
                    <KV k="Query embed" v={result.timing_breakdown?.embed_query_ms != null ? `${result.timing_breakdown.embed_query_ms} ms` : "—"} />
                    <KV k="Chunk pool fetch" v={result.timing_breakdown?.chunk_pool_fetch_ms != null ? `${result.timing_breakdown.chunk_pool_fetch_ms} ms` : "—"} />
                    <KV k="Cosine score (all)" v={result.timing_breakdown?.cosine_score_all_ms != null ? `${result.timing_breakdown.cosine_score_all_ms} ms` : "—"} />
                  </div>
                </div>
              )}
            </Section>
          )}

          {result.target_doc_focus && result.target_doc_focus.length > 0 && (
            <Section title={`Target document focus (matched doc_hint="${result.doc_hint}")`} testid="diag-target">
              {result.target_doc_focus.map((d) => {
                const det = (result.target_doc_chunks || {})[d.doc_id] || {};
                return (
                  <div key={d.doc_id} className="border border-sand-200 rounded-xl p-4 mb-3 last:mb-0">
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-semibold text-ink">{d.title}</div>
                      <div className="flex gap-1.5">
                        <Chip ok={d.in_indexed_bucket} text={d.in_indexed_bucket ? "indexed bucket" : "in-flight bucket"} />
                        {d.gate_reason && <Chip ok={false} text={d.gate_reason} />}
                      </div>
                    </div>
                    <div className="text-xs text-ink/60 mb-2">
                      status={d.status} · summary_len={d.summary_len} · chunks_in_db={det.total_chunks_in_db ?? "?"} ·
                      chunks_in_pool={det.chunks_in_pool ?? "?"} · chunks_in_top_evidence={det.chunks_in_top_evidence ?? 0} ·
                      made_top_cut={det.made_top_evidence_doc_cut ? "yes" : "no"}
                    </div>
                    <div className="text-xs text-ink/60 mb-2 flex flex-wrap gap-x-3 gap-y-1">
                      <span>ocr_engine=<b className="font-mono">{d.ocr_engine || "native"}</b></span>
                      <span>ocr_used=<b className="font-mono">{String(d.ocr_used)}</b></span>
                      <span>word_count=<b className="font-mono tabular-nums">{d.word_count?.toLocaleString?.() || 0}</b></span>
                      <span>content_length=<b className="font-mono tabular-nums">{d.content_length?.toLocaleString?.() || 0}</b></span>
                      {(d.word_count >= 95000) && (
                        <span className="text-rose-700 font-semibold">⚠ likely truncated (pre-iter200 600k cap)</span>
                      )}
                    </div>
                    {det.chunks && det.chunks.length > 0 && (
                      <div className="mt-3 overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-ink/50 uppercase text-[10px] tracking-wider">
                              <th className="text-left py-1.5 pr-3">Section</th>
                              <th className="text-left py-1.5 pr-3">Preview</th>
                              <th className="text-right py-1.5 pr-3">Cosine</th>
                              <th className="text-center py-1.5 pr-3">In pool</th>
                              <th className="text-center py-1.5">In top</th>
                            </tr>
                          </thead>
                          <tbody>
                            {det.chunks.slice(0, 20).map((c) => (
                              <tr key={c.chunk_id} className="border-t border-sand-100">
                                <td className="py-1.5 pr-3 font-mono text-[11px] text-ink/70">{c.section || "—"}</td>
                                <td className="py-1.5 pr-3 text-ink/80 max-w-md truncate">{c.text_preview}</td>
                                <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{c.cosine_vs_query ?? "n/a"}</td>
                                <td className="py-1.5 pr-3 text-center">{c.in_candidate_pool ? "✓" : "·"}</td>
                                <td className="py-1.5 text-center">{c.in_top_evidence ? "★" : "·"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {det.chunks.length > 20 && (
                          <div className="text-[11px] text-ink/40 mt-2 italic">
                            (showing top 20 of {det.chunks.length} chunks by score)
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </Section>
          )}

          {result.top_evidence && result.top_evidence.length > 0 && (
            <Section title={`Top evidence — what the LLM actually saw`} testid="diag-top">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-ink/50 uppercase text-[10px] tracking-wider">
                    <th className="text-left py-1.5 pr-3">Rank</th>
                    <th className="text-left py-1.5 pr-3">Document</th>
                    <th className="text-left py-1.5 pr-3">Section</th>
                    <th className="text-right py-1.5 pr-3">Cosine</th>
                    <th className="text-left py-1.5">Preview</th>
                  </tr>
                </thead>
                <tbody>
                  {result.top_evidence.map((e, i) => (
                    <tr key={e.chunk_id} className="border-t border-sand-100">
                      <td className="py-1.5 pr-3 font-mono text-ink/60">{i + 1}</td>
                      <td className="py-1.5 pr-3 text-ink font-medium">{e.title || "—"}</td>
                      <td className="py-1.5 pr-3 font-mono text-[11px] text-ink/70">{e.section || "—"}</td>
                      <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{e.score}</td>
                      <td className="py-1.5 text-ink/70 max-w-md truncate">{e.text_preview}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Section>
          )}

          <details className="bg-white rounded-2xl border border-sand-200 p-5">
            <summary className="cursor-pointer text-[11px] font-bold uppercase tracking-wider text-ink/60 flex items-center gap-2">
              <Info className="h-3.5 w-3.5" /> Raw JSON (full)
            </summary>
            <pre className="mt-3 text-[11px] font-mono bg-sand-50 rounded-lg p-4 overflow-x-auto max-h-96 leading-relaxed">
              {JSON.stringify(result, null, 2)}
            </pre>
          </details>
        </div>
      )}
    </div>
  );
}
